import { Spacer, Text } from "@hackthenorth/north";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { Modal } from "src/shared/components";
import { CircleCheckmark, Gift, Map, PointsIcon } from "src/static/icons";

import FooterActions from "../../../../footerActions";

import { QUESTS } from "./constants";

interface LearnMoreModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TOTAL_STEPS = 4;

const LearnMoreModal: React.FC<LearnMoreModalProps> = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(0);
  const isFirstStep = step === 0;
  const isLastStep = step === TOTAL_STEPS - 1;

  useEffect(() => {
    if (isOpen) setStep(0);
  }, [isOpen]);

  const overlayStyle = {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    paddingTop: "72px",
    paddingBottom: "72px",
    overflowY: "auto" as const,
  };

  return (
    <Modal
      title="Welcome to Goose Games"
      isOpen={isOpen}
      overlayStyle={overlayStyle}
      onClose={onClose}
    >
      <ProgressBar>
        {Array.from({ length: TOTAL_STEPS }, (_, i) => (
          <ProgressSegment key={i} isActive={i <= step} />
        ))}
      </ProgressBar>

      {step === 0 && (
        <PlaceholderStep>
          <Spacer height={12} />
          <StyledTitle mods="heading h3 body">
            Your mini-adventure inside Hack the North
          </StyledTitle>
          <Text mods="grey">
            Goose Games is a fun, low-stakes way to explore, engage, and earn
            awesome rewards while you&apos;re at Hack the North.
          </Text>
        </PlaceholderStep>
      )}

      {step === 1 && (
        <>
          <Spacer height={12} />
          <StyledTitle mods="heading h3 body">Complete Quests</StyledTitle>
          <Text mods="grey lh-small">
            Earn points by completing activities throughout the event. Each
            quest will give you points; some are automatic (you will complete
            them by simply participating in Hack the North), others need a
            little detective work.
          </Text>
          <QuestWrapper>
            {QUESTS.map((quest) => (
              <QuestDetails key={quest.id}>
                <QuestItem>
                  <QuestImageWrapper color={quest.backgroundColor}>
                    <quest.image height={24} width={24} color={quest.color} />
                  </QuestImageWrapper>
                  <QuestTitle color={quest.color}>{quest.title}</QuestTitle>
                </QuestItem>
                <Text mods="blueGrey bold">{quest.description}</Text>
              </QuestDetails>
            ))}
          </QuestWrapper>
        </>
      )}

      {step === 2 && (
        <PlaceholderStep>
          <Spacer height={12} />
          <StyledTitle mods="heading h3 body">Track your progress</StyledTitle>
          <Text mods="grey">Check the dashboard to:</Text>
          <Spacer height={12} />
          <ProgressList>
            <li>
              <CircleCheckmark />
              <Text mods="grey">See which quests you&apos;ve finished</Text>
            </li>
            <li>
              <Map />
              <Text mods="grey">Discover what&apos;s still out there</Text>
            </li>
            <li>
              <PointsIcon variant="gray" />
              <Text mods="grey">View your point balance</Text>
            </li>
            <li>
              <Gift />
              <Text mods="grey">Unlock special achievements</Text>
            </li>
          </ProgressList>
        </PlaceholderStep>
      )}

      {step === 3 && (
        <PlaceholderStep>
          <Spacer height={12} />
          <StyledTitle mods="heading h3 body">Redeem Prizes</StyledTitle>
          <Text mods="grey lh-small">
            Trade your points for
            <strong> exclusive swag </strong>
            at the <strong>Goose Games Store</strong>!
          </Text>
          <Spacer height={12} />
          <Text mods="grey lh-small">
            Check out the &quot;Store&quot; tab to browse available prizes, then
            head over to the Help Desk to redeem them IRL. No online checkout
            needed.
          </Text>
          <Spacer height={12} />
          <Text mods="grey lh-small">
            <strong>Are you ready to start gaming?</strong>
          </Text>
        </PlaceholderStep>
      )}

      <BottomActionContainer>
        <FooterActions
          onCancel={step > 0 ? () => setStep((s) => s - 1) : undefined}
          cancelText="Back"
          onConfirm={() => (isLastStep ? onClose() : setStep((s) => s + 1))}
          confirmText={
            isLastStep
              ? "Start gaming!"
              : isFirstStep
              ? "Here's how it works →"
              : "Next →"
          }
          isSecondaryBack={false}
        />
      </BottomActionContainer>
    </Modal>
  );
};

export default LearnMoreModal;

const StyledTitle = styled(Text)`
  color: ${({ theme }) =>
    theme.globalConstants.color.borderGrayDark} !important;
`;

const BottomActionContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
  padding: 0 16px;
`;

const ProgressList = styled.ul`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin: 0;
  padding: 0;
  list-style: none;

  ${({ theme }) => theme.mediaQueries.largeMobile`
    grid-template-columns: 1fr;
  `}

  li {
    display: flex;
    align-items: flex-start; /* Aligns emoji with top line of wrapped text */
    gap: 10px;
    font-weight: 700;

    span[role="img"] {
      font-size: 1.1rem;
      line-height: 1.3; /* Matches text line-height for vertical alignment */
      flex-shrink: 0; /* Prevents emoji from shrinking when text wraps */
    }
  }
`;

const ProgressBar = styled.div`
  display: flex;
  gap: 8px;
`;

const ProgressSegment = styled.div<{ isActive: boolean }>`
  flex: 1;
  height: 10px;
  border-radius: 32px;
  background-color: ${({ theme, isActive }) =>
    isActive
      ? theme.globalConstants.color.backgroundPrimary
      : theme.globalConstants.color.backgroundPrimaryLight};
`;

const PlaceholderStep = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 200px;
`;

const QuestWrapper = styled.div`
  padding: 12px 16px;
  margin-bottom: 12px;

  ${({ theme }) => theme.mediaQueries.largeMobile`
    padding: 12px 0;
  `}
`;

const QuestItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const QuestDetails = styled.div`
  display: grid;
  grid-template-columns: 144px minmax(0, 1fr);
  gap: 12px;
  align-items: center;
  padding: 8px 0;

  ${({ theme }) => theme.mediaQueries.largeMobile`
    grid-template-columns: minmax(0, 1fr);
    align-items: start;
  `}
`;

const QuestImageWrapper = styled.div<{ color: string }>`
  height: 32px;
  width: 32px;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50px;
  background-color: ${({ color }) => color};
`;

const QuestTitle = styled.p<{ color: string }>`
  color: ${({ color }) => color};
  font-weight: 700;
  font-size: 16px;
  margin: 0;
`;
